import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Zap, Crown, Loader2, ShieldCheck, ArrowRight, Sparkles, CreditCard, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/nav";
import { api } from "@/lib/api";
import { getStoredPromo } from "@/lib/promos";
import { cn } from "@/lib/utils";

const PRO_FEATURES = [
    "Live AI Tactical Coach with real-time callouts",
    "Post-Match AI Reviews & improvement tracking",
    "Career Mode, Road To Pro & AI Scout",
    "Auto Clips + Reel Builder",
    "100 GB Cloud Storage",
];
const CREATOR_FEATURES = [
    "Everything in Pro",
    "Content Studio & Highlight Reels",
    "AI Titles, Descriptions & Hashtags",
    "Priority AI Processing",
    "500 GB Cloud Storage",
];

export default function Onboarding() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [cat, setCat] = useState(null);
    const [annual, setAnnual] = useState(false);
    const [busy, setBusy] = useState("");

    const ent = user?.entitlements || {};
    const promo = getStoredPromo();

    useEffect(() => {
        sessionStorage.removeItem("ww_onboard");
        // Owner or already-subscribed users skip onboarding entirely.
        if (user?.is_owner || ent.has_pro) {
            navigate("/dashboard", { replace: true });
            return;
        }
        api.get("/billing/catalog").then(({ data }) => setCat(data)).catch(() => setCat({ plans: [] }));
    }, [user, ent.has_pro, navigate]);

    const startTrial = async (planKey) => {
        setBusy(planKey);
        try {
            const promo_code = getStoredPromo() || undefined;
            const { data } = await api.post("/billing/checkout", { plan_key: planKey, origin: window.location.origin, promo_code });
            window.location.href = data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start your trial. Please try again.");
            setBusy("");
        }
    };

    const priceFor = (tier) => cat?.plans?.find((p) => p.key === `${tier}_${annual ? "annual" : "monthly"}`);
    const pro = priceFor("pro");
    const creator = priceFor("creator");

    return (
        <div className="min-h-screen bg-obsidian text-white grid place-items-center px-4 py-10" data-testid="onboarding-page">
            <div className="w-full max-w-3xl">
                <div className="flex items-center justify-center gap-2.5 mb-7">
                    <img src={LOGO_URL} alt="WhiteWater AI" className="w-9 h-9" />
                    <div className="font-heading font-bold tracking-tight text-lg">WhiteWater<span className="text-volt">AI</span></div>
                </div>

                <div className="text-center mb-7">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-volt/10 border border-volt/30 mb-4">
                        <ShieldCheck className="w-4 h-4 text-volt" />
                        <span className="text-xs font-mono uppercase tracking-wider text-volt">7-Day Free Trial</span>
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Welcome, {user?.first_name || "player"}!</h1>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Pick a plan to start your <span className="text-white font-semibold">7-day free trial</span>. A card is required to start —
                        <span className="text-white"> $0 today</span>, and you can cancel anytime before it ends. We'll only charge when the trial finishes.
                    </p>
                </div>

                {promo && (
                    <div className="max-w-md mx-auto mb-6 flex items-center justify-center gap-2.5 rounded-sm border border-volt/40 bg-volt/[0.08] px-3.5 py-2.5" data-testid="onboarding-promo-banner">
                        <Tag className="w-4 h-4 text-volt shrink-0" />
                        <p className="text-sm text-white">Promo <span className="font-mono font-bold text-volt">{promo}</span> applies automatically at checkout.</p>
                    </div>
                )}

                {/* Billing interval toggle */}
                <div className="flex items-center justify-center gap-3 mb-7" data-testid="onboarding-interval-toggle">
                    <button onClick={() => setAnnual(false)} className={cn("text-sm font-semibold transition-colors", !annual ? "text-white" : "text-muted-foreground")}>Monthly</button>
                    <button onClick={() => setAnnual(!annual)} data-testid="onboarding-toggle-annual"
                        className="relative w-12 h-6 rounded-full bg-surface border border-border">
                        <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-volt transition-all", annual ? "left-[26px]" : "left-1")} />
                    </button>
                    <button onClick={() => setAnnual(true)} className={cn("text-sm font-semibold transition-colors flex items-center gap-1.5", annual ? "text-white" : "text-muted-foreground")}>
                        Annual <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-success/15 text-success border border-success/30">SAVE ~17%</span>
                    </button>
                </div>

                {!cat ? (
                    <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading plans…</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {/* Pro */}
                        <div className="relative rounded-sm border border-border bg-surface p-6" data-testid="onboarding-plan-pro">
                            <div className="flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-volt" /><h3 className="font-heading text-xl font-bold">WhiteWater Pro</h3></div>
                            <p className="text-sm text-muted-foreground mb-4">For competitive players & ranked grinders.</p>
                            <div className="mb-5">
                                <span className="font-heading text-4xl font-bold text-white">${annual ? "149" : "14.99"}</span>
                                <span className="text-muted-foreground text-sm">/{annual ? "year" : "month"}</span>
                                <div className="text-xs text-volt mt-1">$0 for 7 days, then ${annual ? "149/yr" : "14.99/mo"}</div>
                            </div>
                            <button onClick={() => pro && startTrial(pro.key)} disabled={!!busy || !pro} data-testid="onboarding-start-pro"
                                className="w-full h-11 rounded-sm bg-volt text-black font-bold uppercase tracking-wide hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                                {busy === pro?.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Start Free Trial
                            </button>
                            <ul className="mt-5 space-y-2">
                                {PRO_FEATURES.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-success mt-0.5 shrink-0" />{f}</li>)}
                            </ul>
                        </div>

                        {/* Creator */}
                        <div className="relative rounded-sm border-2 border-volt/60 bg-surface p-6" data-testid="onboarding-plan-creator">
                            <span className="absolute -top-3 left-6 text-[10px] font-mono uppercase px-2.5 py-1 rounded-sm bg-volt text-black font-bold">Most Popular</span>
                            <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-volt" /><h3 className="font-heading text-xl font-bold">WhiteWater Creator</h3></div>
                            <p className="text-sm text-muted-foreground mb-4">For streamers, YouTubers & content creators.</p>
                            <div className="mb-5">
                                <span className="font-heading text-4xl font-bold text-white">${annual ? "249" : "24.99"}</span>
                                <span className="text-muted-foreground text-sm">/{annual ? "year" : "month"}</span>
                                <div className="text-xs text-volt mt-1">$0 for 7 days, then ${annual ? "249/yr" : "24.99/mo"}</div>
                            </div>
                            <button onClick={() => creator && startTrial(creator.key)} disabled={!!busy || !creator} data-testid="onboarding-start-creator"
                                className="w-full h-11 rounded-sm bg-volt text-black font-bold uppercase tracking-wide hover:bg-[#33ceff] flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
                                {busy === creator?.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Start Free Trial
                            </button>
                            <ul className="mt-5 space-y-2">
                                {CREATOR_FEATURES.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="w-4 h-4 text-success mt-0.5 shrink-0" />{f}</li>)}
                            </ul>
                        </div>
                    </div>
                )}

                <div className="text-center mt-7">
                    <button onClick={() => navigate("/dashboard")} data-testid="onboarding-skip"
                        className="text-sm text-muted-foreground hover:text-white inline-flex items-center justify-center gap-1.5">
                        Maybe later — continue with limited free access <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <p className="text-[11px] text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
                        <CreditCard className="w-3 h-3 text-volt" /> Card secured by Stripe · $0 today · cancel anytime during the trial.
                    </p>
                </div>
            </div>
        </div>
    );
}
